import { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * User preferences for the bimodal reader's comet-tail highlight engine.
 *
 * Persisted to localStorage so settings survive refresh. Not tied to user
 * account (yet) — these are per-device preferences.
 *
 * - leadLetters: distance in letters from the audio position to the peak
 *   of the green focal zone (Eye-Voice Span). Tighter values keep focus
 *   close to the spoken word; wider values allow pre-processing.
 * - dyslexiaMode: when on, forces leadLetters to DYSLEXIA_LEAD and
 *   switches the reading font to OpenDyslexic.
 */

// Tight-focus option set. 0 = peak on the spoken word; 1 = one letter
// ahead; 2 = the dyslexia-mode default (tight focus reduces visual
// crowding); 4 = comfortable default for regular readers; 8 = upper
// bound for readers who pre-process several letters ahead.
export const LEAD_LETTER_OPTIONS = [0, 1, 2, 4, 8];
const DEFAULT_LEAD = 4;
const DYSLEXIA_LEAD = 2;

const STORAGE_KEY = 'readwell.readingPreferences.v1';

const ReadingPreferencesContext = createContext(null);

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!LEAD_LETTER_OPTIONS.includes(parsed.leadLetters)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveToStorage(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Quota / private mode — fine to ignore
  }
}

export function ReadingPreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(() => {
    const stored = loadFromStorage();
    return stored || { leadLetters: DEFAULT_LEAD, dyslexiaMode: false };
  });

  useEffect(() => {
    saveToStorage(prefs);
  }, [prefs]);

  const setLeadLetters = useCallback((n) => {
    if (!LEAD_LETTER_OPTIONS.includes(n)) return;
    setPrefs((p) => ({ ...p, leadLetters: n }));
  }, []);

  const setDyslexiaMode = useCallback((enabled) => {
    setPrefs((p) => ({
      ...p,
      dyslexiaMode: enabled,
      // Toggling dyslexia ON auto-tightens the lead to 9 per neuroscience
      // research on visual crowding. Users can still manually change it after.
      leadLetters: enabled ? DYSLEXIA_LEAD : p.leadLetters,
    }));
  }, []);

  const value = {
    leadLetters: prefs.leadLetters,
    dyslexiaMode: prefs.dyslexiaMode,
    setLeadLetters,
    setDyslexiaMode,
  };

  return (
    <ReadingPreferencesContext.Provider value={value}>
      {children}
    </ReadingPreferencesContext.Provider>
  );
}

export function useReadingPreferences() {
  const ctx = useContext(ReadingPreferencesContext);
  if (!ctx) {
    throw new Error('useReadingPreferences must be used inside ReadingPreferencesProvider');
  }
  return ctx;
}
